import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { AnimalWhereaboutModel } from '../models/animal-whereabout.model';
import { AnimalModel } from '../models/animal.model';
import { AnimalWhereaboutsRepository, animalWhereaboutsStore } from '../states/animal-whereabouts.repository';
import { catchError, concat, finalize, forkJoin, lastValueFrom, map, Observable, of, switchMap, take, takeWhile, throwError, toArray } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { PersonTitleModel } from "../../../shared/models/person-title.model";
import { ContactRepository } from "../../contact/states/contact.repository";
import { ContactModel, PhoneNumbersEntryType } from "../../contact/models/contact.model";
import { HelperService } from "../../../shared/services/helper.service";
import { ConfirmationService } from 'primeng/api';
import { ToastService } from 'src/app/shared/services/toast.service';
import { AnimalRepository } from '../states/animal.repository';
import { RestService } from 'src/app/shared/services/rest.service';
import { FoundReportPdfRepository, SendFoundReportPayload } from '../states/pdf.repository';

import { AnimalSheltersRepository } from '../../../shared/states/animal-shelter.repository';
import { AnimalShelterModel } from "../../../shared/models/animal-shelter.model";
import { select } from '@ngneat/elf';
import { DiscoverySource } from '../models/discovery-source.enum';
import { AnimalWhereaboutsType } from '../models/animal-wherabout-types.enum';
import { Subject } from 'rxjs';
import { AnimalContactRef } from '../models/animal-contact-ref.interface';
import { AnimalContactTypeEnum } from '../models/contact-type.enum';
import { CARE_OPTIONS, CareCondition, NUTRITION_OPTIONS, NutritionCondition, TEMPERAMENT_OPTIONS, TemperamentCondition } from '../models/body-condition-score.model';
import { SentEmailModel } from '../models/sent-email.model';

export interface NewAnimalWhereaboutItem {
  id: string;
  animal: { id: number };
  type: string | null;
  date: Date | null;
  contacts: AnimalContactRef[];
  dateCare: Date | null;
  information: string | null;
  externalDescription: string | null;
}

@Component({
  selector: 'app-animal-details-whereabouts',
  templateUrl: './animal-details-whereabouts.component.html',
  styleUrls: ['./animal-details-whereabouts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AnimalDetailsWhereaboutsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() animal!: AnimalModel;
  @Input() singleItemId: number | null = null;
  @Input() addModeOnly: boolean = false;
  @Output() formsValid: EventEmitter<boolean> = new EventEmitter();
  alive: boolean = true;
  objectKeys = Object.keys;
  isEditingMode: boolean = false;
  isAddMode: boolean = false;
  animalWhereaboutsItems: { [id: string]: AnimalWhereaboutModel | any } = {};
  whereAboutTypeOptions: { value: string, name: string }[] = [];
  selectedTypeOption: { [id: string]: { value: string, name: string } } = {};
  personSalutationOptions: PersonTitleModel[] = [];

  contacts: { [id: string]: ContactModel } = {};
  editingItem: any;
  openEdit: boolean = false;
  editType: any;
  editDialogWhereAboutKey: number | null = null;

  contact: ContactModel = {} as ContactModel;
  existingContact: boolean = false;
  validationStatusChanged: boolean = true;
  discoverSourceOptions: { value: string, name: string }[] = [];
  selectedDiscoveryOption: { [id: string]: { value: string, name: string } } = {};
  hasUnsavedData: boolean = false;
  animalSheltersById: { [id: number]: AnimalShelterModel } = {};
  phoneNumbersEntryType = PhoneNumbersEntryType;
  contactTypeOptions: { value: string; name: string }[] = [];

  readonly careOptions = CARE_OPTIONS;
  readonly nutritionOptions = NUTRITION_OPTIONS;
  readonly temperamentOptions = TEMPERAMENT_OPTIONS;

  editDialogContactRefIndex: number | null = null;
  editingContactRef: AnimalContactRef | null = null;

  dummyContactRefs: { ref: AnimalContactRef; contact: ContactModel | null; isExisting: boolean }[] = [];
  isDummyContactMode = false;
  editingDummyContactRefIndex: number | null = null;

  // Pickup report flow
  isFoundAnimalReturnSelected = false;
  confirmDataDialogVisible = false;
  pickUpReportDialogVisible = false;
  protected readonly AnimalWhereaboutsType = AnimalWhereaboutsType;

  // Pickup PDF / send
  pickupPdfDialogVisible = false;
  pickupPdfBlob: Blob | null = null;
  sendPickupReportDialogVisible = false;
  sendPickupReportWhereaboutId: number | null = null;
  sendPickupReportContacts: ContactModel[] = [];
  sendPickupReportAuthorityInformation: string | null = null;
  sendPickupReportFn: ((id: number, payload: SendFoundReportPayload) => import('rxjs').Observable<any>) | null = null;

  sentEmailsByWhereaboutId: { [whereaboutId: number]: SentEmailModel[] } = {};

  sentEmailsDialogVisible = false;
  sentEmailsDialogData: SentEmailModel[] = [];

  openSentEmailsDialog(event: Event, emails: SentEmailModel[]): void {
    event.preventDefault();
    event.stopPropagation();
    this.sentEmailsDialogData = emails ?? [];
    this.sentEmailsDialogVisible = true;
  }

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly _animalWhereaboutsRepository: AnimalWhereaboutsRepository,
    private readonly _contactRepository: ContactRepository,
    private readonly _translocoService: TranslocoService,
    private readonly _helperService: HelperService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _confirmationService: ConfirmationService,
    private readonly _animalRepository: AnimalRepository,
    private readonly _toast: ToastService,
    private readonly _animalSheltersRepository: AnimalSheltersRepository,
    private readonly _restService: RestService,
    private readonly _pdfRepo: FoundReportPdfRepository,
  ) {
    this.sendPickupReportFn = (id, payload) => this._pdfRepo.sendPickupReport(id, payload);
  }

  async ngOnInit(): Promise<void> {
    await this._helperService.ensureActiveLangLoaded();

    this.buildSelectOptions();

    if (this.singleItemId !== null) {
      this.initSingleItemMode();
      this.getWhereabouts();
      return;
    }
    if (this.addModeOnly) return;
    this.getWhereabouts();
  }

  private initSingleItemMode(): void {
    animalWhereaboutsStore.pipe(
      select(state => state.whereabouts?.[this.animal?.id] ?? []),
      takeWhile(() => this.alive)
    ).subscribe(whereabouts => {
      const filtered = (whereabouts ?? []).filter(w => w.id === this.singleItemId);
      if (filtered.length) {
        this.setAnimalWhereabouts(filtered);
      }
    });
  }


  private buildSelectOptions(reAssignSelected = false): void {
    this.whereAboutTypeOptions = this._helperService.getEnumOptions(
      AnimalWhereaboutsType,
      'animalWhereabout.type',
      true
    );

    this.contactTypeOptions = this._helperService.getEnumOptions(
      AnimalContactTypeEnum as any,
      'animalContact.types',
      true
    );

    this.discoverSourceOptions = this._helperService.getEnumOptions(
      DiscoverySource,
      'animalWhereabout.discoverySource',
      false
    );

    if (reAssignSelected) {
      Object.entries(this.animalWhereaboutsItems).forEach(([id, item]: any) => {
        if (item?.type) {
          const found = this.whereAboutTypeOptions.find(o => o.value === item.type);
          if (found) this.selectedTypeOption[id] = found;
        }
        if (item?.discoverySource) {
          const foundD = this.discoverSourceOptions.find(o => o.value === item.discoverySource);
          if (foundD) this.selectedDiscoveryOption[id] = foundD;
        }
      });
    }

    this.personSalutationOptions = this._helperService.getSalutationOptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.singleItemId !== null) return;
    if (this.addModeOnly) return;
    if (changes['animal'] && this.animal?.id) {
      this.getWhereabouts();
    }
  }

  private getWhereabouts(): void {
    if (!this.animal?.id) {
      return;
    }
    this.animalWhereaboutsItems = {};
    this._animalWhereaboutsRepository.getByAnimalId(this.animal.id, false)
      .pipe(takeWhile(() => this.alive))
      .subscribe((animalWhereabouts) => {
        if (Object.keys(animalWhereabouts).length) {
          // Shelter-IDs sammeln
          const shelterIds = Array.from(new Set(Object.values(animalWhereabouts)
            .filter((w: any) => w.animalShelter?.id != null)
            .map((w: any) => w.animalShelter.id)));
          if (shelterIds.length > 0) {
            this.loadSheltersByIds(shelterIds).then(() => {
              this.setAnimalWhereabouts(animalWhereabouts);
            });
          } else {
            this.setAnimalWhereabouts(animalWhereabouts);
          }
        }
      });
    if (this.animal.id) {
      animalWhereaboutsStore.pipe(
        select(state => state.whereabouts?.[this.animal.id] ?? []),
        takeWhile(() => this.alive)
      ).subscribe(whereabouts => {
        this.setAnimalWhereabouts(whereabouts);
      });
    }
  }

  private loadSheltersByIds(ids: number[]): Promise<void> {
    return lastValueFrom(
      forkJoin(ids.map(id => this._animalSheltersRepository.get(id))).pipe(
        map(shelters => {
          shelters.forEach(shelter => {
            if (shelter?.id) this.animalSheltersById[shelter.id] = shelter;
          });
        })
      )
    );
  }

  setAnimalWhereabouts(animalWhereabouts: AnimalWhereaboutModel[]) {
    // items reset nicht hier, nur updaten
    const allContactIds = Array.from(new Set(
      (animalWhereabouts ?? [])
        .flatMap(w => (w.contacts ?? []).map(r => r.contact))
        .filter((id): id is number => typeof id === 'number')
    ));

    const contacts$ = allContactIds.length
      ? forkJoin(allContactIds.map(id => this._contactRepository.getById(id).pipe(take(1))))
      : of([]);

    contacts$.pipe(take(1)).subscribe((loaded: ContactModel[]) => {
      (loaded ?? []).forEach(c => {
        if (!c?.id) return;
        this.contacts[c.id] = c;

        const b = this.contacts[c.id].birthday;
        if (typeof b === 'string') this.contacts[c.id].birthday = new Date(b);
      });

      (animalWhereabouts ?? []).forEach((w: AnimalWhereaboutModel) => {
        if (!w?.id) return;

        const clone = JSON.parse(JSON.stringify(w)) as AnimalWhereaboutModel;

        clone.date = clone.date ? new Date(clone.date as any) : null;
        clone.dateCare = clone.dateCare ? new Date(clone.dateCare as any) : null;

        // Sicherstellen, dass contacts existiert
        (clone as any).contacts = Array.isArray((clone as any).contacts) ? (clone as any).contacts : [];

        this.animalWhereaboutsItems[w.id] = clone;

        // selected options
        const foundTypeOption = this.whereAboutTypeOptions.find(o => o.value === clone.type);
        if (foundTypeOption) this.selectedTypeOption[w.id] = foundTypeOption;

        const foundDiscoveryOption = this.discoverSourceOptions.find(o => o.value === clone.discoverySource);
        if (foundDiscoveryOption) this.selectedDiscoveryOption[w.id] = foundDiscoveryOption;
      });

      const foundAnimalReturnIds = (animalWhereabouts ?? [])
        .filter(w => w.type === AnimalWhereaboutsType.FOUNDANIMAL_RETURN && w.id)
        .map(w => w.id);
      if (foundAnimalReturnIds.length) {
        this.loadSentEmails(foundAnimalReturnIds);
      }

      this._cdr.detectChanges();
    });
  }

  toggleEditDialog(open: boolean, editType: string, whereAboutKey: number | null, contactKey?: number | null) {
    if (open) {
      if (editType === 'general' && whereAboutKey !== null && this.animalWhereaboutsItems[whereAboutKey]) {
        this.editingItem = JSON.parse(JSON.stringify(this.animalWhereaboutsItems[whereAboutKey]));
        this.editingItem.date = this.editingItem.date ? new Date(this.editingItem.date) : null;
        this.editingItem.dateCare = this.editingItem.dateCare ? new Date(this.editingItem.dateCare) : null;
      } else if (editType === 'contactRef') {
        const refIdx = contactKey !== null && contactKey !== undefined ? contactKey : null;
        this.editDialogContactRefIndex = refIdx;

        if (refIdx !== null && whereAboutKey !== null) {
          const whereAbout = this.animalWhereaboutsItems[whereAboutKey];
          const ref: AnimalContactRef | undefined = (whereAbout?.contacts ?? [])[refIdx];
          if (ref) {
            this.editingContactRef = { ...ref };
            const contactId = ref.contact;
            if (contactId && this.contacts[contactId]) {
              this.contact = JSON.parse(JSON.stringify(this.contacts[contactId]));
              this.existingContact = true;
            } else {
              this.contact = {} as ContactModel;
              this.existingContact = false;
            }
          } else {
            this.editingContactRef = { type: AnimalContactTypeEnum.OWNER, label: '', isPrimary: false };
            this.contact = {} as ContactModel;
            this.existingContact = false;
          }
        } else {
          this.editingContactRef = { type: AnimalContactTypeEnum.OWNER, label: '', isPrimary: false };
          this.contact = {} as ContactModel;
          this.existingContact = false;
        }
        this.editingItem = this.contact;
      }

      this.validationStatusChanged = true;

      this.editType = editType;
      this.editDialogWhereAboutKey = whereAboutKey;
      this.isEditingMode = true;
      this.openEdit = open;
    } else {
      this.validationStatusChanged = true;
      this.openEdit = open;
      this.editDialogWhereAboutKey = -1;
      this.isEditingMode = false;
      this.isDummyContactMode = false;
      this.editingDummyContactRefIndex = null;
    }
  }


  openDummyContactDialog(index: number | null): void {
    this.isDummyContactMode = true;
    this.editingDummyContactRefIndex = index;
    this.editType = 'contactRef';

    if (index !== null && this.dummyContactRefs[index]) {
      const entry = this.dummyContactRefs[index];
      this.editingContactRef = { ...entry.ref };
      this.contact = entry.contact ? JSON.parse(JSON.stringify(entry.contact)) : {} as ContactModel;
      this.existingContact = entry.isExisting;
    } else {
      this.editingContactRef = { type: AnimalContactTypeEnum.OWNER, label: '', isPrimary: false };
      this.contact = {} as ContactModel;
      this.existingContact = false;
    }
    this.editingItem = this.contact;
    this.validationStatusChanged = true;
    this.openEdit = true;
    this.isEditingMode = true;
    this._cdr.detectChanges();
  }

  removeDummyContactRef(index: number): void {
    this.dummyContactRefs.splice(index, 1);
    this._cdr.detectChanges();
  }

  onContactUpdate($event: any) {
    this.contact = $event;
  }

  onValidationStatusChanged($event: boolean) {
    this.validationStatusChanged = $event;
  }

  onExistingContactSelected($event: ContactModel) {
    this.contact = $event;
    this.existingContact = Object.keys($event).length ? true : false;
  }


  setUnsavedChanges(value: boolean) {
    this.hasUnsavedData = value;
  }

  addNewWhereabout(): void {
    if (!this.animal) {
      return;
    }
    this.setUnsavedChanges(true);
    const emptyWhereabout: NewAnimalWhereaboutItem = {
      id: 'dummy',
      animal: { id: this.animal?.id } as any,
      type: null,
      date: null,
      contacts: [],
      dateCare: null,
      information: null,
      externalDescription: null
    };

    this.editingItem = {} as ContactModel;
    (this.animalWhereaboutsItems['dummy'] as NewAnimalWhereaboutItem) = emptyWhereabout;
    this.isAddMode = true;
    this._cdr.detectChanges();
  }

  cancelNewWhereabout() {
    delete this.animalWhereaboutsItems['dummy'];
    delete this.selectedTypeOption['dummy'];
    delete this.selectedDiscoveryOption['dummy'];
    this.isAddMode = false;
    this.isFoundAnimalReturnSelected = false;
    this.editingItem = null;
    this.contact = {} as ContactModel;
    this.dummyContactRefs = [];
    this.isDummyContactMode = false;
    this.editingDummyContactRefIndex = null;
    this.setUnsavedChanges(false);
    this._cdr.detectChanges();
  }

  onDummyTypeChange(option: { value: string; name: string }): void {
    this.isFoundAnimalReturnSelected = option?.value === AnimalWhereaboutsType.FOUNDANIMAL_RETURN;
    this._cdr.markForCheck();
  }

  openPickUpReportConfirm(): void {
    this.confirmDataDialogVisible = true;
  }

  onConfirmDataCurrent(): void {
    this.confirmDataDialogVisible = false;
    this.pickUpReportDialogVisible = true;
  }

  onPickUpReportCompleted(): void {
    this.pickUpReportDialogVisible = false;
    this.cancelNewWhereabout();
    this.getWhereabouts();
  }

  getCareLabel(value: CareCondition): string {
    const key = this.careOptions.find(o => o.value === value)?.description;
    return key ? this._translocoService.translate(key) : value;
  }

  getNutritionLabel(value: NutritionCondition): string {
    const key = this.nutritionOptions.find(o => o.value === value)?.description;
    return key ? this._translocoService.translate(key) : value;
  }

  getTemperamentLabel(value: TemperamentCondition): string {
    const key = this.temperamentOptions.find(o => o.value === value)?.description;
    return key ? this._translocoService.translate(key) : value;
  }

  isFoundAnimalReturn(key: string): boolean {
    return this.animalWhereaboutsItems[key]?.type === AnimalWhereaboutsType.FOUNDANIMAL_RETURN;
  }

  openPickupPdf(event: Event, key: string): void {
    event.preventDefault();
    event.stopPropagation();
    const path = (this.animalWhereaboutsItems[key] as any)?.reportPdfPath;
    if (!path) return;
    this._restService.httpGet(`/assets/${path}`, { responseType: 'blob' }).pipe(take(1)).subscribe(blob => {
      this.pickupPdfBlob = blob;
      this.pickupPdfDialogVisible = true;
      this._cdr.detectChanges();
    });
  }

  openSendPickupReport(event: Event, key: string): void {
    event.preventDefault();
    event.stopPropagation();
    const whereabout = this.animalWhereaboutsItems[key];
    this.sendPickupReportWhereaboutId = whereabout?.id ?? null;
    const contactIds = (whereabout?.contacts ?? [])
      .map((r: any) => r.contact)
      .filter((id: any): id is number => typeof id === 'number');
    this.sendPickupReportContacts = contactIds.map((id: number) => this.contacts[id]).filter(Boolean);
    this.sendPickupReportAuthorityInformation = whereabout?.authorityInformation ?? null;
    this.sendPickupReportDialogVisible = true;
    this._cdr.markForCheck();
  }

  onSendPickupReportSent(authorityInformation: string | null): void {
    this.sendPickupReportDialogVisible = false;
    const whereaboutId = this.sendPickupReportWhereaboutId;
    if (whereaboutId && this.animalWhereaboutsItems[whereaboutId]) {
      const updated = {
        ...this.animalWhereaboutsItems[whereaboutId],
        reportSentOn: new Date(),
        authorityInformation: authorityInformation ?? null
      };
      this.animalWhereaboutsItems[whereaboutId] = updated;
      this.loadSentEmails([whereaboutId]);
      this._animalWhereaboutsRepository.update(whereaboutId, updated).pipe(take(1)).subscribe();
      this._cdr.detectChanges();
    }
  }

  private loadSentEmails(whereaboutIds: number[]): void {
    whereaboutIds.forEach(id => {
      this._restService.httpGet(`/sent_emails?animalWhereaboutsId=${id}`)
        .pipe(take(1))
        .subscribe((emails: SentEmailModel[]) => {
          this.sentEmailsByWhereaboutId[id] = emails ?? [];
          this._cdr.detectChanges();
        });
    });
  }

  saveNewWhereabout() {
    if (!this.selectedTypeOption['dummy']?.value) {
      return;
    }
    const dummy = this.animalWhereaboutsItems['dummy'];

    const payload: any = {
      type: this.selectedTypeOption['dummy']?.value,
      date: dummy.date ?? null,
      dateCare: dummy.dateCare ?? null,
      discoverySource: this.selectedDiscoveryOption['dummy']?.value,
      information: dummy.information,
      externalDescription: dummy.externalDescription ?? null,
      animal: { id: this.animal?.id } as any,
      contacts: [] as AnimalContactRef[],
    };

    const resolveEntry = (entry: typeof this.dummyContactRefs[0]): Observable<AnimalContactRef> => {
      const { ref, contact } = entry;

      if (contact?.id) {
        const contactId = contact.id;
        return this._contactRepository.update(contact, contactId).pipe(
          map(updated => ({ ...ref, contact: this.cacheContact(updated, contact, contactId).id }))
        );
      }

      return this._contactRepository.create(contact!).pipe(
        map(created => ({ ...ref, contact: this.cacheContact(created).id }))
      );
    };

    const entries = this.dummyContactRefs.filter(e => this.isFilledContact(e.contact));

    const request$ = entries.length === 0
      ? this._animalWhereaboutsRepository.create(payload)
      : concat(...entries.map(e => resolveEntry(e))).pipe(
          toArray(),
          switchMap(resolvedRefs => {
            payload.contacts = resolvedRefs;
            return this._animalWhereaboutsRepository.create(payload);
          })
        );

    request$
      .pipe(
        take(1),
        catchError((error) => {
          this._toast.showError(this._translocoService.translate('toast.saveError'));
          console.error('Fehler beim Speichern des Aufenthaltsortes:', error);
          return throwError(() => error);
        }),
        finalize(() => {
          this._cdr.detectChanges();
        })
      )
      .subscribe((response) => {
        this.cancelNewWhereabout();
        this.handleResponse(response);
        this._toast.showSuccess(this._translocoService.translate('toast.saveSuccess'));
      });
  }


  handleResponse(response: any) {
    response.date = response.date
      ? new Date(response.date)
      : null;
    response.dateCare = response.dateCare
      ? new Date(response.dateCare)
      : null;
    this.animalWhereaboutsItems = {
      ...this.animalWhereaboutsItems,
      [response.id]: response
    };
    const foundTypeOption = this.whereAboutTypeOptions.find(option => option.value === response.type);
    if (foundTypeOption) {
      this.selectedTypeOption[response.id] = foundTypeOption;
    }
    const foundDiscoveryOption = this.discoverSourceOptions.find(option => option.value === response.discoverySource);
    if (foundDiscoveryOption) {
      this.selectedDiscoveryOption[response.id] = foundDiscoveryOption;
    }
    if (this.animal?.id) {
      this._animalRepository.updateAnimalStore(this.animal.id, { updatedAt: new Date() });
    }
    this._cdr.detectChanges();
  }


  private isFilledContact(contact: ContactModel | null): boolean {
    return !!contact && !!(contact.id || contact.firstName || contact.lastName || contact.companyName);
  }

  private cacheContact(resp: ContactModel, fallback?: ContactModel, fallbackId?: number): ContactModel {
    const persisted = resp?.id ? resp : ({ ...fallback, id: fallbackId } as ContactModel);
    this.contacts[persisted.id] = persisted;

    const b = this.contacts[persisted.id]?.birthday;
    if (typeof b === 'string') this.contacts[persisted.id].birthday = new Date(b);

    return persisted;
  }

  private upsertContactRefAndUpdateWhereabout(
    whereAboutId: number,
    refIndex: number | null,
    refDraft: AnimalContactRef,
    contactPayload: ContactModel
  ): Observable<AnimalWhereaboutModel> {
    const current = this.animalWhereaboutsItems[String(whereAboutId)] as AnimalWhereaboutModel | undefined;
    if (!current) {
      return throwError(() => new Error('Whereabout not found in local state'));
    }

    const applyUpdate = (contactId: number | null) => {
      const nextRefs = [...(current.contacts ?? [])];

      const nextRef: AnimalContactRef = {
        ...refDraft,
        contact: contactId
      };

      if (refIndex == null) {
        nextRefs.push(nextRef);
      } else {
        nextRefs[refIndex] = nextRef;
      }

      const payload: AnimalWhereaboutModel = {
        ...current,
        contacts: nextRefs
      };

      return this._animalWhereaboutsRepository.update(whereAboutId, payload).pipe(
        map((resp: any) => {
          resp.date = resp.date ? new Date(resp.date) : null;
          resp.dateCare = resp.dateCare ? new Date(resp.dateCare) : null;

          this.animalWhereaboutsItems = {
            ...this.animalWhereaboutsItems,
            [whereAboutId]: resp
          };

          return resp as AnimalWhereaboutModel;
        })
      );
    };

    if (contactPayload?.id) {
      const contactId = contactPayload.id;
      return this._contactRepository.update(contactPayload, contactId).pipe(
        switchMap((updated) => applyUpdate(this.cacheContact(updated, contactPayload, contactId).id))
      );
    }

    if (!contactPayload || Object.keys(contactPayload).length === 0) {
      return applyUpdate(typeof refDraft.contact === 'number' ? refDraft.contact : null);
    }

    return this._contactRepository.create(contactPayload).pipe(
      switchMap((created) => applyUpdate(this.cacheContact(created).id))
    );
  }

  save(animalWhereAboutItem: AnimalWhereaboutModel) {
    if (this.editType === 'general') {
      this.updateAnimalWhereAbout(animalWhereAboutItem);
      return;
    }

    if (this.editType !== 'contactRef') {
      return;
    }

    if (this.isDummyContactMode) {
      if (!this.editingContactRef) return;
      const contactToStore = (this.contact && Object.keys(this.contact).length) ? { ...this.contact } : null;
      const entry = {
        ref: { ...this.editingContactRef },
        contact: contactToStore,
        isExisting: this.existingContact
      };
      if (this.editingDummyContactRefIndex !== null && this.editingDummyContactRefIndex < this.dummyContactRefs.length) {
        this.dummyContactRefs[this.editingDummyContactRefIndex] = entry;
      } else {
        this.dummyContactRefs.push(entry);
      }
      this.toggleEditDialog(false, '', null, null);
      this._cdr.detectChanges();
      return;
    }

    const whereId = this.editDialogWhereAboutKey;
    if (whereId == null) return;
    if (!this.editingContactRef) return;

    this.upsertContactRefAndUpdateWhereabout(
      whereId,
      this.editDialogContactRefIndex,
      this.editingContactRef,
      this.contact
    ).pipe(
      take(1),
      catchError((error) => {
        this._toast.showError(this._translocoService.translate('toast.saveError'));
        console.error('Fehler beim Speichern ContactRef/Whereabout:', error);
        return throwError(() => error);
      }),
      finalize(() => this._cdr.detectChanges())
    ).subscribe(() => {
      this.toggleEditDialog(false, '', null, null);
      this._toast.showSuccess(this._translocoService.translate('toast.saveSuccess'));
    });
  }



  updateAnimalWhereAbout(animalWhereAboutItem: AnimalWhereaboutModel | any): void {
    if (!this.animal || !this.animal.id || !this.editDialogWhereAboutKey) {
      return;
    }
    if (!this.selectedTypeOption[animalWhereAboutItem.id]?.value) {
      return;
    }
    let date = null;
    if (animalWhereAboutItem.date) {
      date = this._helperService.determineDateOfReceipt(
        this.animalWhereaboutsItems[this.editDialogWhereAboutKey].date,
        animalWhereAboutItem.date
      );
    }
    let dateCare = null;
    if (animalWhereAboutItem.dateCare) {
      dateCare = this._helperService.determineDateOfReceipt(
        this.animalWhereaboutsItems[this.editDialogWhereAboutKey].dateCare,
        animalWhereAboutItem.dateCare
      );
    }

    const whereAboutPayload: AnimalWhereaboutModel = {
      ...animalWhereAboutItem,
      date,
      dateCare,
      animal: { id: this.animal.id },
      contact: animalWhereAboutItem.contact && animalWhereAboutItem.contact.id ? { id: animalWhereAboutItem.contact.id } : null,
      type: this.selectedTypeOption[animalWhereAboutItem.id].value,
      discoverySource: this.selectedDiscoveryOption[animalWhereAboutItem.id]?.value,
      information: animalWhereAboutItem.information,
      externalDescription: animalWhereAboutItem.externalDescription ?? null
    };

    const key = String(this.editDialogWhereAboutKey);

    this._animalWhereaboutsRepository.update(this.editDialogWhereAboutKey, whereAboutPayload).pipe(
      take(1),
      catchError((error) => {
        this._toast.showError(this._translocoService.translate('toast.saveError'));
        return throwError(() => error);
      })
    )
      .subscribe(() => {
        this.animalWhereaboutsItems = {
          ...this.animalWhereaboutsItems,
          [key]: whereAboutPayload
        };

        this.toggleEditDialog(false, '', null);
        this._toast.showSuccess(this._translocoService.translate('toast.saveSuccess'));
        this._cdr.detectChanges();
      });
  }

  deleteWhereabout(id: number) {
    if (!this.animal?.id) {
      return;
    }

    this._confirmationService.confirm({
      message: this._translocoService.translate('delete_confirmation'),
      acceptLabel: this._translocoService.translate('yes'),
      rejectLabel: this._translocoService.translate('no'),
      header: '',
      icon: 'pi pi-info-circle',
      acceptButtonStyleClass: "p-button-danger p-button-text",
      rejectButtonStyleClass: "p-button-text p-button-text",
      acceptIcon: "none",
      rejectIcon: "none",
      accept: () => {
        this._animalWhereaboutsRepository.delete(id, this.animal!.id)
          .pipe(take(1))
          .subscribe(() => {
            delete this.animalWhereaboutsItems[id];
            this._toast.showSuccess(this._translocoService.translate('toast.deleteSuccess'));
            this._cdr.detectChanges();
          });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
